import type { ChangeLogEntry } from '@prm/shared';

const KIND_LABEL: Record<string, string> = {
  verdict: 'VERDICT',
  band: 'BAND',
  posture: 'POSTURE',
  'breach-new': 'BREACH',
  'breach-cleared': 'CLEARED',
  'cluster-hardened': 'CLUSTER',
  gate: 'GATE',
};

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });
}

/**
 * The recorded transitions, newest first.
 *
 * Only observations where something crossed appear — a run where nothing moved
 * writes a snapshot but no log entry, so a week of drift reads as the handful of
 * moments that mattered rather than fourteen identical rows.
 */
export function ChangesView({ entries }: { entries: ChangeLogEntry[] }) {
  if (entries.length === 0) {
    return (
      <div className="placeholder-note">
        No transitions recorded yet. The scheduled run writes a snapshot twice each weekday and logs an entry only
        when something crosses — a verdict, a band, a posture flip, a breach appearing or clearing, a cluster
        hardening, or a gate changing state.
      </div>
    );
  }

  return (
    <>
      <div className="changelog">
        {entries.map((entry) => (
          <div className="changelog__entry" key={entry.takenAt}>
            <span className="changelog__when dim">{when(entry.takenAt)}</span>
            <div className="changelog__items">
              {entry.changes.map((c) => (
                <div className="changelog__item" key={c.id}>
                  <span className={`minichip minichip--${c.severity === 'danger' ? 'breach' : c.severity === 'warn' ? 'warn' : 'ok'}`}>
                    {KIND_LABEL[c.kind] ?? c.kind}
                  </span>
                  <span className="changelog__text">{capitalize(c.sentence)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="footnote">
        Times are UTC. A change that repeats within the cooldown window is recorded but kept out of the headline, so a
        value oscillating around a boundary does not report as news every session.
      </div>
    </>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
